import { ColumnDefinition, reactFormatter } from "react-tabulator";
import { ReportTypes } from "../../api";
import { CellClamp } from "./CellClamp";

export enum Presets {
  LastMonth = 0,
  Month = 30,
  Week = 7,
  Day = 1,
};

export enum ReportSource {
  Leads = 0,
  Clio = 1
}

export interface ReportDetails {
  type: ReportTypes;
  description: string;
  header: string;
  columns: ColumnDefinition[];
  showDateRange: boolean;
}

export const AllSelection = "Select all";

export const ReportsData: ReportDetails[] = [{
  header: "Leads by staff",
  description: "Shows intakes made by staff and what stage they are currently in.",
  type: ReportTypes.LEADS_STAFF,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "Name", field: "userName" },
    { title: "Practice area", field: "practiceArea" },
    { title: "Lead status", field: "status" },
    { title: "Lead name", field: "leadName" },
    {
      title: "Created at",
      field: "createdAt",
      sorter: "date",
      sorterParams: {
        format: "MM-dd-yyyy",
        alignEmptyValues: "bottom",
      },
    },
  ],
  showDateRange: true
}, {
  header: "Leads by attorney",
  description: `Shows intakes reviewed by attorneys and what decision was made. Referred leads are included in approval rate. 
Lead review time tracking started on 06/13/24. Leads before that date are excluded from average response time.`,
  type: ReportTypes.LEADS_ATTORNEY,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "Name", field: "userName" },
    { title: "Approval rate", field: "percentage" },
    { title: "Lead status", field: "status" },
    { title: "Lead name", field: "leadName" },
    {
      title: "Created at",
      field: "createdAt",
      sorter: "date",
      sorterParams: {
        format: "MM-dd-yyyy",
        alignEmptyValues: "bottom",
      },
    },
    { title: "Average response time", field: "average", width: 150 },
  ],
  showDateRange: true
}, {
  header: "Intake overview",
  description: "Shows intakes grouped by status and outcome for the selected date range.",
  type: ReportTypes.LEADS_DATE,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "Completed", field: "completed", width: 120 },
    { title: "Lead status", field: "status" },
    { title: "Lead name", field: "leadName" },
    { title: "Practice area", field: "practiceArea" },
    {
      title: "Created at",
      field: "createdAt",
      sorter: "date",
      sorterParams: {
        format: "MM-dd-yyyy",
        alignEmptyValues: "bottom",
      },
    },
  ],
  showDateRange: true
}, {
  header: "Staff matters overview",
  description: `Shows how many matters staff have assigned to them and who else works on their matters. 
As an example, if an attorney is selected, this report shows total matters and how many they share with each paralegal.`,
  type: ReportTypes.OVERVIEW,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "Task Assignee", field: "descriptionLevel1" },
    { title: "Funnel", field: "descriptionLevel2" },
    { title: "Sub-Funnel", field: "descriptionLevel3", width: 150 },
    { title: "Matter Stage", field: "descriptionLevel4" },
    { title: "Matter Name", field: "descriptionLevel5", width: 300 },
    { title: "Task Name", field: "descriptionLevel6" },
  ],
  showDateRange: false
}, {
  header: "Staff pending tasks",
  description: "Shows how many tasks are currently assigned to staff.",
  type: ReportTypes.TASKS,
  columns: [
    { title: "Total", field: "total", width: 120 },
    { title: "Task Assignee", field: "descriptionLevel1" },
    { title: "Funnel", field: "descriptionLevel2", width: 100 },
    { title: "Status", field: "descriptionLevel3", width: 100 },
    { title: "Task Type/Name", field: "descriptionLevel4" },
    { title: "Matter Name", field: "descriptionLevel5", width: 290 },
    { title: "Due Date", field: "level5Data3", width: 100 },
    { title: "Overdue", field: "level5Data4" },
    { title: "SOLE", field: "level5Data2", width: 100 },
    { title: "Conversion", field: "level5Data1", width: 100 },
  ],
  showDateRange: true
}, {
  header: "Staff hours",
  description: `Shows time tracked by staff for funnels, grouped by tasks and activities. 
Clio rounds time up, which can diverge over long date ranges. Values appear as "Real hours || Clio time".`,
  type: ReportTypes.HOURS,
  columns: [
    { title: "Hours || Clio", field: "totalHours", width: 150 },
    { title: "Name", field: "descriptionLevel1", width: 200 },
    { title: "Funnel", field: "descriptionLevel2", width: 100 },
    { title: "Task Name", field: "descriptionLevel3" },
    { title: "Matter Name", field: "level4Data1", width: 200 },
    { title: "Details", field: "descriptionLevel4", width: 400 },
    { title: "Task Completion", field: "level4Data2", width: 100 },
  ],
  showDateRange: true
}, {
  header: "Task extensions report",
  description: `Shows how many extensions staff have and whether they were approved. Statuses:
  Requested — Extension requested through Glio.
  Unauthorized — Due date changed in Clio without an extension request.
  Approved / Authorized / Rejected — Admin decision; due date updated automatically when applicable.
  Task Removed — Extension was requested or unauthorized, and the task is now deleted.`,
  type: ReportTypes.EXTENSIONS,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "Name", field: "descriptionLevel1", width: 150 },
    { title: "Status", field: "descriptionLevel2", width: 150 },
    { title: "Task Name", field: "descriptionLevel3", width: 250 },
    { title: "Matter Name", field: "descriptionLevel4", width: 250 },
    { title: "Original", field: "level3Data1", width: 100 },
    { title: "Request", field: "level3Data2", width: 100 },
    { title: "Message", field: "level3Data3", formatter: "textarea", width: 200 },
  ],
  showDateRange: true
}, {
  header: "Matter stage changes",
  description: "Shows manual matter stage changes made by staff.",
  type: ReportTypes.MATTER_STAGE_CHANGE,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "Name", field: "descriptionLevel1", width: 200 },
    { title: "Type", field: "descriptionLevel2", width: 100 },
    { title: "Matter Name", field: "descriptionLevel3", width: 400 },
    { title: "Changes", field: "descriptionLevel4", width: 500 },
  ],
  showDateRange: true
}, {
  header: "Empty matters",
  description: "Shows matters that currently have no pending tasks assigned. This may be expected in weekly follow-up stages.",
  type: ReportTypes.EMPTY_MATTERS,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "Funnel", field: "descriptionLevel1", width: 200 },
    { title: "Attorney", field: "descriptionLevel2", width: 200 },
    { title: "SA", field: "level3Data2", width: 100 },
    { title: "Matter Name", field: "descriptionLevel3", width: 250 },
    { title: "Matter Stage", field: "level3Data1", width: 250 },
    { title: "Last Update", field: "level3Data3", width: 200 },
  ],
  showDateRange: false
}, {
  header: "Inactive matters",
  description: "Shows matters with no activity in the last 3 weeks.",
  type: ReportTypes.INACTIVE_MATTERS,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "Funnel", field: "descriptionLevel1", width: 200 },
    { title: "Attorney", field: "descriptionLevel2", width: 200 },
    { title: "SA", field: "level3Data2", width: 100 },
    { title: "Matter Name", field: "descriptionLevel3", width: 250 },
    { title: "Matter Stage", field: "level3Data1", width: 250 },
    { title: "Last Update", field: "level3Data3", width: 200 },
  ],
  showDateRange: false
},{
  header: "Task assignee changes",
  description: "Shows how task assignees were changed.",
  type: ReportTypes.TASK_ASSIGNEE_CHANGE,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "User name", field: "descriptionLevel1", width: 150 },
    { title: "Changes", field: "level2Data1", width: 300 },
    { title: "Task Name", field: "level2Data2", width: 350 },
    { title: "Matter Name", field: "descriptionLevel2", width: 400 },
  ],
  showDateRange: true
}, {
  header: "Pre-lit settlements",
  description: "Shows settlement matters that moved from pre-lit funnels into settlement.",
  type: ReportTypes.SETTLEMENT_PRELIT,
  columns: [
    { title: "Total", field: "total", width: 150 },
    { title: "Month", field: "descriptionLevel1", width: 150 },
    { title: "Total Settlement", field: "totalClio", width: 150 },
    { title: "Attorney", field: "descriptionLevel2", width: 150 },
    { title: "Status", field: "descriptionLevel3", width: 150 },
    { title: "Matter Stage", field: "descriptionLevel4", width: 250 },
    { title: "Matter Name", field: "descriptionLevel5", width: 250 },
    { title: "Pmt. rec.", field: "level5Data1", width: 100 },
    { title: "Attorney's Fee", field: "level5Data2", width: 150 },
  ],
  showDateRange: true
},
{
  header: "Litigation settlements",
  description: "Shows settlement matters that moved from litigation into settlement. Litigation separation started on 06/18/2024; earlier matters appear in the pre-lit settlements report.",
  type: ReportTypes.SETTLEMENT_LIT,
  columns: [
    { title: "Total", field: "total", width: 150 },
    { title: "Month", field: "descriptionLevel1", width: 150 },
    { title: "Total Settlement", field: "totalClio", width: 150 },
    { title: "Attorney", field: "descriptionLevel2", width: 150 },
    { title: "Status", field: "descriptionLevel3", width: 130 },
    { title: "Matter Stage", field: "descriptionLevel4", width: 150 },
    { title: "Team", field: "level5Data2", width: 210, formatter: "textarea" },
    { title: "Matter Name", field: "descriptionLevel5", width: 220 },
    { title: "Pmt. rec.", field: "level5Data1", width: 100 },
    { title: "Attorney's Fee", field: "level5Data2", width: 120 },
  ],
  showDateRange: true
},
{
  header: "Pre-lit KPIs",
  description: `Demand, complaint, warning, and termination stages are tracked from 07/02/2024.`,
  type: ReportTypes.KPI,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "Month", field: "descriptionLevel1", width: 150 },
    { title: "Stage", field: "descriptionLevel2", width: 200 },
    { title: "Matter Name", field: "descriptionLevel3" },
    { title: "Completed at", field: "level3Data1", width: 200 }
  ],
  showDateRange: true
},
{
  header: "Negotiation outcomes",
  description: `Where and when a matter moved after negotiations. Date is based on when the matter left negotiation.`,
  type: ReportTypes.NEGOTIATION_RESULT,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "Month", field: "descriptionLevel1", width: 200 },
    { title: "Funnel", field: "descriptionLevel2", width: 200 },
    { title: "Stage", field: "level3Data1", width: 200 },
    { title: "Days in Negotiations", field: "level3Data2", width: 200 },
    { title: "Matter", field: "descriptionLevel3" },
  ],
  showDateRange: true
},
{
  header: "SOLES report",
  description: `Overview of Statutes of Limitations Expiration (SOLES) for active pre-litigation cases, so critical deadlines stay visible and actionable.`,
  type: ReportTypes.SOLES,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "Month", field: "descriptionLevel1", width: 100 },
    { title: "Attorney", field: "descriptionLevel2", width: 150 },
    { title: "Matter", field: "descriptionLevel3", width: 150, formatter: "textarea" },
    { title: "Termination", field: "level3Data7", width: 70 },
    { title: "Conversion", field: "level3Data8", width: 70 },
    { title: "Team", field: "level3Data1", width: 150, formatter: "textarea" },
    { title: "Earliest", field: "level3Data2", width: 70 },
    { title: "WTVPP", field: "level3Data3", width: 70 },
    { title: "FEHA", field: "level3Data4", width: 70 },
    { title: "W&H", field: "level3Data5", width: 70 },
    { title: "UCL", field: "level3Data6", width: 70 },
    { title: "Notes", field: "level3Data9" },
  ],
  showDateRange: true
},{
  header: "Litigation funnel overview",
  description: "Shows litigation matters and tasks across the funnel.",
  type: ReportTypes.LITIGATION_FUNNEL,
  columns: [
    { title: "Total", field: "total", width: 70 },
    { title: "Attorney", field: "descriptionLevel1", width: 100 },
    { title: "Team", field: "level2Data1", width: 150, formatter: "textarea" },
    { title: "Matter", field: "level2Data2", width: 130, formatter: "textarea" },
    { title: "Funnel", field: "level2Data3", width: 150, formatter: "textarea" },
    { title: "Stage", field: "level2Data4", width: 130, formatter: "textarea" },
    { title: "Task", field: "level2Data5", width: 200, formatter: "textarea" },
    { title: "Assignee", field: "level2Data6", width: 100, formatter: "textarea" },
    { title: "Due Date", field: "level2Data7", width: 100, formatter: "textarea" },
    { 
      title: "Upcoming Deadlines", 
      field: "level2Data8", 
      formatter: reactFormatter(
        <CellClamp maxLines={2} minChars={120} />
      )
    },
    { title: "Overdue", field: "level2Data9", width: 100 },
  ],
  showDateRange: false
},{
  header: "Negotiation log",
  description: "Shows proof of communications for negotiation in litigation.",
  type: ReportTypes.NEGOTIATION_LOG,
  columns: [
    { title: "Total", field: "total", width: 100 },
    { title: "Attorney", field: "descriptionLevel1", width: 100 },
    { title: "Matter", field: "level2Data1", width: 150, formatter: "textarea" },
    { title: "Type", field: "level3Data1", width: 150, formatter: "textarea" },
    { title: "Total Duration", field: "level3Data2", width: 100, formatter: "textarea" },
    { title: "Date", field: "level4Data1", width: 130, formatter: "textarea" },
    { title: "Duration", field: "level4Data2", width: 100, formatter: "textarea" },
    { title: "Subject", field: "level4Data3", formatter: "textarea" },
  ],
  showDateRange: false
},
];

